import React, { useState, useEffect } from "react";
import { Modal, Spinner, Alert, Pagination } from "react-bootstrap";
import axios from "axios";
import Hoot from "../../Hoot";
import { toast } from "react-hot-toast";
import {
  MdPhone,
  MdBusiness,
  MdImage,
  MdCheck,
  MdClose,
  MdBlock,
  MdSearch,
  MdRefresh,
  MdChevronLeft,
  MdChevronRight,
  MdFirstPage,
  MdLastPage,
  MdAccessTime,
  MdCalendarToday,
  MdChatBubbleOutline,
} from "react-icons/md";

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const styles = `
  @keyframes pulseAmber {
    0%, 100% {
      background: rgba(234, 179, 8, 0.12);
      border-color: rgba(234, 179, 8, 0.55);
      box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.45);
    }
    50% {
      background: rgba(234, 179, 8, 0.22);
      border-color: rgba(234, 179, 8, 0.9);
      box-shadow: 0 0 0 7px rgba(234, 179, 8, 0);
    }
  }

  /* ── trigger button ── */
  .ref-trigger-btn {
    padding: 9px 20px;
    font-size: 0.9rem;
    font-weight: 600;
    border-radius: 999px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    background: #fff;
    color: #0f172a;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: background 0.15s, border-color 0.15s;
  }
  .ref-trigger-btn:hover {
    background: rgba(248, 250, 252, 0.9);
    border-color: rgba(148, 163, 184, 0.7);
  }
  .ref-trigger-btn.has-pulse {
    animation: pulseAmber 2s ease-in-out infinite;
  }

  .ref-count-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.35);
    font-size: 0.72rem;
    font-weight: 700;
  }

  /* ── modal overrides ── */
  .ref-modal .modal-dialog {
    max-width: 100vw !important;
    width: 100vw !important;
    height: 100vh !important;
    margin: 0 !important;
  }
  .ref-modal .modal-content {
    border-radius: 0 !important;
    border: none !important;
    height: 100vh;
    display: flex;
    flex-direction: column;
    box-shadow: none !important;
    background: #f8fafc;
  }
  .ref-modal .modal-header {
    border-bottom: 1px solid rgba(226, 232, 240, 0.9);
    background: #ffffff;
    padding: 14px 24px;
    flex-shrink: 0;
  }
  .ref-modal .modal-body {
    padding: 0;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  /* ── header ── */
  .ref-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
  }
  .ref-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .ref-avatar {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: rgba(91, 92, 255, 0.08);
    border: 1px solid rgba(91, 92, 255, 0.18);
    display: grid;
    place-items: center;
    flex-shrink: 0;
    color: #5b5cff;
  }
  .ref-title { font-weight: 700; font-size: 1rem; color: #0f172a; }
  .ref-subtitle { font-size: 0.78rem; color: #64748b; margin-top: 1px; }

  /* ── content area ── */
  .ref-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  /* ── filters ── */
  .ref-filters {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
    margin-bottom: 10px;
  }
  .ref-filter-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 5px;
  }
  .ref-input {
    width: 100%;
    padding: 7px 11px;
    border-radius: 10px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    font-size: 0.85rem;
    color: #0f172a;
    background: #fff;
    outline: none;
    transition: border-color 0.15s;
  }
  .ref-input:focus { border-color: rgba(91, 92, 255, 0.45); }

  .ref-filter-actions {
    display: flex;
    gap: 8px;
    margin-bottom: 18px;
    flex-wrap: wrap;
  }
  .ref-action-pill {
    padding: 6px 14px;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    background: #fff;
    color: #475569;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background 0.15s, border-color 0.15s;
  }
  .ref-action-pill:hover {
    background: #f1f5f9;
    border-color: rgba(148, 163, 184, 0.7);
  }
  .ref-action-pill:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── cards ── */
  .ref-cards { display: flex; flex-direction: column; gap: 12px; }

  .ref-card {
    background: #fff;
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 16px;
    padding: 14px 16px;
    transition: box-shadow 0.2s, border-color 0.2s;
  }
  .ref-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.07);
    border-color: rgba(148, 163, 184, 0.5);
  }
  .ref-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }
  .ref-card-name { font-weight: 700; font-size: 0.95rem; color: #0f172a; }
  .ref-card-meta {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-top: 4px;
  }
  .ref-card-meta-row {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.8rem;
    color: #64748b;
  }

  .ref-institution {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.88rem;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 10px;
    color: #334155;
  }

  .ref-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 8px;
    font-size: 0.78rem;
    margin-bottom: 10px;
  }
  .ref-detail-label { color: #64748b; font-weight: 600; margin-bottom: 2px; }
  .ref-detail-val   { color: #0f172a; font-weight: 500; }

  /* ── images ── */
  .ref-images-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .ref-thumb {
    width: 72px;
    height: 72px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid rgba(226, 232, 240, 0.95);
    cursor: pointer;
    transition: transform 0.2s, border-color 0.2s;
    flex-shrink: 0;
  }
  .ref-thumb:hover { transform: scale(1.06); border-color: rgba(91, 92, 255, 0.4); }
  .ref-thumb img  { width: 100%; height: 100%; object-fit: cover; display: block; }

  /* ── badges ── */
  .ref-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 11px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
    border: 1px solid;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .ref-badge-pending  { background: rgba(234, 179, 8, 0.12); color: #a16207; border-color: rgba(234, 179, 8, 0.45); }
  .ref-badge-approved { background: rgba(34, 197, 94, 0.12); color: #15803d; border-color: rgba(34, 197, 94, 0.4); }
  .ref-badge-rejected { background: rgba(239, 68, 68, 0.12); color: #dc2626; border-color: rgba(239, 68, 68, 0.4); }
  .ref-badge-cancelled{ background: rgba(100, 116, 139, 0.1); color: #64748b; border-color: rgba(100, 116, 139, 0.35); }

  /* ── card actions ── */
  .ref-card-actions {
    display: flex;
    justify-content: flex-end;
    gap: 7px;
    flex-wrap: wrap;
    padding-top: 10px;
    border-top: 1px solid rgba(226, 232, 240, 0.9);
  }
  .ref-btn {
    padding: 5px 13px;
    font-size: 0.78rem;
    font-weight: 600;
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    background: #fff;
    color: #475569;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: background 0.15s, border-color 0.15s;
  }
  .ref-btn:hover:not(:disabled) { background: #f1f5f9; }
  .ref-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .ref-btn-approve {
    color: #15803d;
    border-color: rgba(34, 197, 94, 0.4);
    background: rgba(34, 197, 94, 0.07);
  }
  .ref-btn-approve:hover:not(:disabled) { background: rgba(34, 197, 94, 0.14); }
  .ref-btn-reject {
    color: #dc2626;
    border-color: rgba(239, 68, 68, 0.4);
    background: rgba(239, 68, 68, 0.07);
  }
  .ref-btn-reject:hover:not(:disabled) { background: rgba(239, 68, 68, 0.14); }
  .ref-btn-cancel {
    color: #64748b;
    border-color: rgba(100, 116, 139, 0.35);
    background: rgba(100, 116, 139, 0.07);
  }
  .ref-btn-cancel:hover:not(:disabled) { background: rgba(100, 116, 139, 0.14); }

  /* ── empty / loading ── */
  .ref-empty {
    text-align: center;
    padding: 60px 20px;
    color: #94a3b8;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .ref-loading {
    text-align: center;
    padding: 60px 20px;
    color: #64748b;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
  }

  /* ── pagination ── */
  .ref-pagination {
    display: flex;
    justify-content: center;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid rgba(226, 232, 240, 0.9);
  }

  /* ── image modal ── */
  .ref-img-modal .modal-content {
    border-radius: 18px !important;
    border: 1px solid rgba(226, 232, 240, 0.95) !important;
    overflow: hidden;
  }
  .ref-img-modal .modal-header { border-bottom: 1px solid rgba(226, 232, 240, 0.9); padding: 12px 16px; }
  .ref-img-modal .modal-body   { padding: 16px; text-align: center; }
  .ref-img-modal img { max-width: 100%; max-height: 70vh; border-radius: 10px; display: block; margin: 0 auto; }
`;

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("pt-PT"); } catch { return "—"; }
};

const formatCurrency = (v) => {
  if (!v) return "—";
  return Number(v).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const StatusBadge = ({ status }) => {
  const map = {
    pending:   { cls: "ref-badge-pending",   icon: <MdAccessTime size={11}/>,  label: "Pendente"  },
    approved:  { cls: "ref-badge-approved",  icon: <MdCheck size={11}/>,        label: "Aprovada"  },
    rejected:  { cls: "ref-badge-rejected",  icon: <MdClose size={11}/>,        label: "Rejeitada" },
    cancelled: { cls: "ref-badge-cancelled", icon: <MdBlock size={11}/>,        label: "Cancelada" },
  };
  const s = map[status] || map.pending;
  return <span className={`ref-badge ${s.cls}`}>{s.icon} {s.label}</span>;
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function ReferralsModalComponent() {
  const [show, setShow]               = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [referrals, setReferrals]     = useState([]);
  const [newCount, setNewCount]       = useState(0);
  const [updatingId, setUpdatingId]   = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });

  const [filters, setFilters] = useState({ status: "", referrer_phone: "", institution_name: "" });

  /* ── polling ── */
  useEffect(() => {
    loadNewCount();
    const id = setInterval(loadNewCount, 30_000);
    return () => clearInterval(id);
  }, []);

  const loadNewCount = async () => {
    try {
      const res = await axios.get(Hoot() + "referrals/count/new", { withCredentials: true });
      if (res.data.success) setNewCount(res.data.count || 0);
    } catch {}
  };

  /* ── load list ── */
  const loadReferrals = async (page = 1) => {
    try {
      setError(null);
      setLoading(true);
      const params = new URLSearchParams({ page, limit: pagination.limit });
      if (filters.status)           params.append("status",           filters.status);
      if (filters.referrer_phone)   params.append("referrer_phone",   filters.referrer_phone);
      if (filters.institution_name) params.append("institution_name", filters.institution_name);

      const res = await axios.get(Hoot() + `referrals/list?${params}`, { withCredentials: true });
      if (res.data.success) {
        setReferrals(res.data.data || []);
        setPagination(res.data.pagination || {});
      } else {
        setError(res.data.message || "Erro ao carregar recomendações.");
      }
    } catch {
      setError("Erro ao carregar recomendações.");
    } finally {
      setLoading(false);
    }
  };

  /* ── update status ── */
  const updateStatus = async (referralId, newStatus) => {
    try {
      setUpdatingId(referralId);
      const res = await axios.put(
        Hoot() + `referrals/${referralId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success("Estado atualizado com sucesso!");
        loadReferrals(pagination.page);
        loadNewCount();
      } else {
        toast.error(res.data.message || "Erro ao atualizar estado.");
      }
    } catch {
      toast.error("Erro ao atualizar estado.");
    } finally {
      setUpdatingId(null);
    }
  };

  const openModal  = () => { setShow(true); loadReferrals(); };
  const closeModal = () => { if (loading || updatingId) return; setShow(false); };

  const handleFilterChange = (field, value) =>
    setFilters((prev) => ({ ...prev, [field]: value }));

  const clearFilters = () => {
    setFilters({ status: "", referrer_phone: "", institution_name: "" });
    loadReferrals(1);
  };

  const hasPulse     = newCount > 0;
  const pendingCount = referrals.filter((r) => r.ed_referral_status === "pending").length;

  /* ─── RENDER ─── */
  return (
    <>
      <style>{styles}</style>

      {/* ── Trigger button ── */}
      <button
        onClick={openModal}
        className={`ref-trigger-btn${hasPulse ? " has-pulse" : ""}`}
      >
        <MdChatBubbleOutline size={17} />
        Recomendações
        {newCount > 0 && (
          <span className="ref-count-dot">{newCount > 99 ? "99+" : newCount}</span>
        )}
      </button>

      {/* ── Main modal (full window) ── */}
      <Modal
        show={show}
        onHide={closeModal}
        backdrop="static"
        className="ref-modal"
        fullscreen
      >
        <Modal.Header closeButton={!loading && !updatingId}>
          <div className="ref-header-row">
            <div className="ref-header-left">
              <div className="ref-avatar">
                <MdChatBubbleOutline size={20} />
              </div>
              <div>
                <div className="ref-title">Recomendações</div>
                <div className="ref-subtitle">
                  {pagination.total} registadas
                  {pendingCount > 0 && ` · ${pendingCount} pendentes`}
                </div>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="ref-body">

            {/* Filtros */}
            <div className="ref-filters">
              <div>
                <label className="ref-filter-label">Estado</label>
                <select
                  className="ref-input"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  disabled={loading}
                >
                  <option value="">Todos</option>
                  <option value="pending">Pendente</option>
                  <option value="approved">Aprovada</option>
                  <option value="rejected">Rejeitada</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>
              <div>
                <label className="ref-filter-label">Telefone</label>
                <input
                  type="text"
                  className="ref-input"
                  placeholder="Pesquisar..."
                  value={filters.referrer_phone}
                  onChange={(e) => handleFilterChange("referrer_phone", e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label className="ref-filter-label">Instituição</label>
                <input
                  type="text"
                  className="ref-input"
                  placeholder="Pesquisar..."
                  value={filters.institution_name}
                  onChange={(e) => handleFilterChange("institution_name", e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Ações de filtro */}
            <div className="ref-filter-actions">
              <button
                className="ref-action-pill"
                onClick={() => loadReferrals(1)}
                disabled={loading}
              >
                <MdSearch size={14} /> Filtrar
              </button>
              <button
                className="ref-action-pill"
                onClick={clearFilters}
                disabled={loading}
              >
                <MdRefresh size={14} /> Limpar
              </button>
            </div>

            {/* Erro */}
            {error && (
              <Alert variant="danger" className="mb-3" style={{ borderRadius: 12 }}>
                {error}
              </Alert>
            )}

            {/* Conteúdo */}
            {loading ? (
              <div className="ref-loading">
                <Spinner animation="border" />
                <span>A carregar recomendações...</span>
              </div>
            ) : referrals.length === 0 ? (
              <div className="ref-empty">
                <MdChatBubbleOutline size={40} />
                <p>Nenhuma recomendação encontrada.</p>
              </div>
            ) : (
              <>
                <div className="ref-cards">
                  {referrals.map((r) => {
                    const isBusy = updatingId === r.ed_referral_id;
                    return (
                      <div key={r.ed_referral_id} className="ref-card">

                        {/* Top row */}
                        <div className="ref-card-top">
                          <div style={{ flex: 1 }}>
                            <div className="ref-card-name">{r.ed_referral_referrer_name}</div>
                            <div className="ref-card-meta">
                              <span className="ref-card-meta-row">
                                <MdPhone size={13} />
                                {r.ed_referral_referrer_phone}
                              </span>
                              <span className="ref-card-meta-row">
                                <MdCalendarToday size={13} />
                                Registada em {formatDate(r.ed_referral_created_at)}
                              </span>
                            </div>
                          </div>
                          <StatusBadge status={r.ed_referral_status} />
                        </div>

                        {/* Instituição */}
                        <div className="ref-institution">
                          <MdBusiness size={15} />
                          {r.ed_referral_institution_name}
                        </div>

                        {/* Detalhes */}
                        <div className="ref-details">
                          <div>
                            <div className="ref-detail-label">Tipo</div>
                            <div className="ref-detail-val">{r.ed_referral_institution_type || "—"}</div>
                          </div>
                          <div>
                            <div className="ref-detail-label">Nível</div>
                            <div className="ref-detail-val">{r.ed_referral_institution_level || "—"}</div>
                          </div>
                          <div>
                            <div className="ref-detail-label">Tamanho</div>
                            <div className="ref-detail-val">{r.ed_referral_institution_size || "—"}</div>
                          </div>
                          <div>
                            <div className="ref-detail-label">Reunião</div>
                            <div className="ref-detail-val">
                              {formatDate(r.ed_referral_meeting_date)}
                              {r.ed_referral_meeting_time ? ` · ${r.ed_referral_meeting_time}` : ""}
                            </div>
                          </div>
                          <div>
                            <div className="ref-detail-label">Comissão est.</div>
                            <div className="ref-detail-val">{formatCurrency(r.ed_referral_estimated_commission)} AOA</div>
                          </div>
                          <div>
                            <div className="ref-detail-label">Presença</div>
                            <div className="ref-detail-val">{r.ed_referral_will_attend ? "Sim" : "Não"}</div>
                          </div>
                        </div>

                        {/* Imagens */}
                        {r.ed_referral_images?.length > 0 && (
                          <>
                            <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
                              <MdImage size={13} />
                              Imagens ({r.ed_referral_images.length})
                            </div>
                            <div className="ref-images-row">
                              {r.ed_referral_images.map((url, idx) => (
                                <div
                                  key={idx}
                                  className="ref-thumb"
                                  onClick={() => { setSelectedImage(url); setShowImageModal(true); }}
                                >
                                  <img
                                    src={url}
                                    alt={`Imagem ${idx + 1}`}
                                    onError={(e) => { e.currentTarget.src = "https://via.placeholder.com/80?text=!"; }}
                                  />
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {/* Ações */}
                        <div className="ref-card-actions">
                          {r.ed_referral_status === "pending" && (
                            <>
                              <button
                                className="ref-btn ref-btn-approve"
                                disabled={isBusy}
                                onClick={() => updateStatus(r.ed_referral_id, "approved")}
                              >
                                {isBusy ? <Spinner size="sm" animation="border" /> : <MdCheck size={14} />}
                                Aprovar
                              </button>
                              <button
                                className="ref-btn ref-btn-reject"
                                disabled={isBusy}
                                onClick={() => updateStatus(r.ed_referral_id, "rejected")}
                              >
                                {isBusy ? <Spinner size="sm" animation="border" /> : <MdClose size={14} />}
                                Rejeitar
                              </button>
                            </>
                          )}
                          {r.ed_referral_status !== "cancelled" && (
                            <button
                              className="ref-btn ref-btn-cancel"
                              disabled={isBusy}
                              onClick={() => updateStatus(r.ed_referral_id, "cancelled")}
                            >
                              {isBusy ? <Spinner size="sm" animation="border" /> : <MdBlock size={14} />}
                              Cancelar
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Paginação */}
                {pagination.pages > 1 && (
                  <div className="ref-pagination">
                    <Pagination>
                      <Pagination.First
                        onClick={() => loadReferrals(1)}
                        disabled={pagination.page === 1 || loading}
                      >
                        <MdFirstPage size={16} />
                      </Pagination.First>
                      <Pagination.Prev
                        onClick={() => loadReferrals(pagination.page - 1)}
                        disabled={pagination.page === 1 || loading}
                      >
                        <MdChevronLeft size={16} />
                      </Pagination.Prev>

                      {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                        const start = Math.max(1, pagination.page - 2);
                        const pg = start + i;
                        return pg <= pagination.pages ? pg : null;
                      })
                        .filter(Boolean)
                        .map((pg) => (
                          <Pagination.Item
                            key={pg}
                            active={pagination.page === pg}
                            onClick={() => loadReferrals(pg)}
                            disabled={loading}
                          >
                            {pg}
                          </Pagination.Item>
                        ))}

                      <Pagination.Next
                        onClick={() => loadReferrals(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages || loading}
                      >
                        <MdChevronRight size={16} />
                      </Pagination.Next>
                      <Pagination.Last
                        onClick={() => loadReferrals(pagination.pages)}
                        disabled={pagination.page === pagination.pages || loading}
                      >
                        <MdLastPage size={16} />
                      </Pagination.Last>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* ── Image viewer modal ── */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
        className="ref-img-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "0.95rem", fontWeight: 600 }}>
            Visualizar imagem
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Imagem ampliada"
              onError={(e) => { e.currentTarget.src = "https://via.placeholder.com/500?text=Erro"; }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}